/**
 * Modal Gestion des Recevables - Procédure holistique B2B
 * Approche qui comprend les deux côtés : Admin (rigueur) et Ventes (relation client)
 */

import { X, DollarSign, FileText, Users, TrendingUp, Brain, Heart, Shield, Target, AlertCircle, CheckCircle, Clock, Send, Scale } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useState } from 'react';

interface GestionRecevablesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const tabs = [
  { id: 'overview', label: 'Vue d\'ensemble', icon: Target },
  { id: 'procedure', label: 'Procédure Détaillée', icon: FileText },
  { id: 'gouvernance', label: 'Gouvernance', icon: Shield },
  { id: 'esprit', label: 'Esprit & Philosophie', icon: Brain },
  { id: 'psychologie', label: 'Psychologie & Impact', icon: Heart },
  { id: 'outils', label: 'Outils & Templates', icon: FileText },
  { id: 'kpis', label: 'KPIs & Métriques', icon: TrendingUp },
];

export default function GestionRecevablesModal({ isOpen, onClose }: GestionRecevablesModalProps) {
  const [activeTab, setActiveTab] = useState('overview');

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl my-8 max-h-[90vh] flex flex-col overflow-hidden"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 text-white p-8 relative">
              <button
                onClick={onClose}
                className="absolute top-4 right-4 p-2 hover:bg-white/20 rounded-full transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
                  <DollarSign className="w-8 h-8" />
                </div>
                <div>
                  <h2 className="text-3xl font-bold mb-2">Gestion des Recevables</h2>
                  <p className="text-emerald-100 text-lg">
                    Procédure holistique B2B - Protection financière & préservation relation client
                  </p>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="border-b border-gray-200 bg-gray-50">
              <div className="flex overflow-x-auto">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`
                        flex items-center gap-2 px-6 py-4 text-sm font-medium transition-colors whitespace-nowrap
                        ${activeTab === tab.id
                          ? 'text-emerald-600 border-b-2 border-emerald-600 bg-white'
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                        }
                      `}
                    >
                      <Icon className="w-4 h-4" />
                      {tab.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-8">
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeTab}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  {activeTab === 'overview' && <OverviewTab />}
                  {activeTab === 'procedure' && <ProcedureTab />}
                  {activeTab === 'gouvernance' && <GouvernanceTab />}
                  {activeTab === 'esprit' && <EspritTab />}
                  {activeTab === 'psychologie' && <PsychologieTab />}
                  {activeTab === 'outils' && <OutilsTab />}
                  {activeTab === 'kpis' && <KPIsTab />}
                </motion.div>
              </AnimatePresence>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}

// Vue d'ensemble
function OverviewTab() {
  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl p-6 border border-emerald-200">
        <h3 className="text-2xl font-bold text-gray-900 mb-4">🎯 Objectif de la Procédure</h3>
        <p className="text-gray-700 text-lg leading-relaxed">
          Établir une procédure claire et équilibrée de gestion des recevables qui <strong>protège la trésorerie de BZ</strong> 
          tout en <strong>préservant les relations client</strong>. Cette procédure reconnaît les enjeux des deux équipes : 
          l'administration qui doit protéger les ressources, et les ventes qui doivent maintenir la confiance client.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
              <Shield className="w-6 h-6 text-red-600" />
            </div>
            <h4 className="text-xl font-bold text-gray-900">Protection Financière</h4>
          </div>
          <ul className="space-y-2 text-gray-700">
            <li className="flex items-start gap-2">
              <CheckCircle className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
              <span>Qualification client selon normes reconnues</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
              <span>Termes de paiement adaptés au risque</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
              <span>Relances structurées et escalade claire</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
              <span>Protection contre les défauts de paiement</span>
            </li>
          </ul>
        </div>

        <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <Users className="w-6 h-6 text-blue-600" />
            </div>
            <h4 className="text-xl font-bold text-gray-900">Relation Client</h4>
          </div>
          <ul className="space-y-2 text-gray-700">
            <li className="flex items-start gap-2">
              <CheckCircle className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
              <span>Communication professionnelle et respectueuse</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
              <span>Compréhension des contraintes client</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
              <span>Solutions flexibles quand approprié</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
              <span>Préservation de la confiance à long terme</span>
            </li>
          </ul>
        </div>
      </div>

      <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
        <h4 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
          <Scale className="w-6 h-6 text-blue-600" />
          L'Équilibre BZ
        </h4>
        <p className="text-gray-700 leading-relaxed">
          Cette procédure reconnaît que <strong>l'administration n'est pas "méchante"</strong> - elle protège les ressources 
          chèrement gagnées par toute l'équipe. Elle reconnaît aussi que <strong>les ventes ne manquent pas de courage</strong> - 
          elles doivent naviguer entre la protection de BZ et la satisfaction client. Cette procédure crée un pont entre ces 
          deux réalités pour le bien de tous.
        </p>
      </div>
    </div>
  );
}

// Procédure détaillée
function ProcedureTab() {
  const steps = [
    {
      id: 1,
      title: "Qualification Client Initiale",
      description: "Évaluation du risque client selon normes reconnues (Dun & Bradstreet, Equifax, Coface, ou système interne)",
      details: [
        "Collecte d'informations : historique de paiement, taille entreprise, secteur d'activité, références bancaires",
        "Analyse du risque selon grille de qualification BZ",
        "Attribution d'un niveau de risque : Faible, Moyen, Élevé",
        "Détermination des termes de paiement initiaux"
      ],
      icon: Target,
      color: "blue"
    },
    {
      id: 2,
      title: "Émission Facture",
      description: "Facturation claire avec termes de paiement visibles",
      details: [
        "Facture émise immédiatement après livraison/prestation",
        "Termes de paiement clairement indiqués (NET 30, NET 45, etc.)",
        "Date d'échéance calculée automatiquement",
        "Envoi automatique par email avec accusé de réception"
      ],
      icon: FileText,
      color: "green"
    },
    {
      id: 3,
      title: "Suivi Pré-Échéance",
      description: "Rappels courtois avant l'échéance",
      details: [
        "J-7 : Rappel automatique courtois par email",
        "J-3 : Second rappel si pas de paiement reçu",
        "Ton professionnel et respectueux",
        "Opportunité de confirmer la réception de la facture"
      ],
      icon: Clock,
      color: "yellow"
    },
    {
      id: 4,
      title: "Relance Post-Échéance",
      description: "Échelle de relances structurées",
      details: [
        "J+3 : Relance courtoise par email",
        "J+7 : Relance téléphonique + email",
        "J+15 : Relance formelle avec copie direction",
        "J+30 : Escalade à la direction + suspension de service possible"
      ],
      icon: Send,
      color: "orange"
    },
    {
      id: 5,
      title: "Escalade & Décisions",
      description: "Processus d'escalade clair avec seuils définis",
      details: [
        "Après J+30 : Réunion Admin + Ventes + Direction",
        "Évaluation de la situation client (difficultés temporaires vs risque réel)",
        "Décision : Plan de paiement, suspension, recouvrement externe",
        "Communication transparente au client"
      ],
      icon: AlertCircle,
      color: "red"
    },
    {
      id: 6,
      title: "Suivi & Amélioration Continue",
      description: "Tracking et ajustements",
      details: [
        "Suivi des KPIs (DSO, taux de recouvrement, etc.)",
        "Révision trimestrielle des procédures",
        "Ajustements selon retours d'expérience",
        "Formation continue des équipes"
      ],
      icon: TrendingUp,
      color: "purple"
    }
  ];

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl p-6 border border-emerald-200">
        <h3 className="text-2xl font-bold text-gray-900 mb-2">📋 Procédure Complète</h3>
        <p className="text-gray-700">
          Processus structuré en 6 étapes, de la qualification initiale au suivi continu.
        </p>
      </div>

      <div className="space-y-6">
        {steps.map((step, index) => {
          const Icon = step.icon;
          const colorClasses = {
            blue: 'bg-blue-100 text-blue-600',
            green: 'bg-green-100 text-green-600',
            yellow: 'bg-yellow-100 text-yellow-600',
            orange: 'bg-orange-100 text-orange-600',
            red: 'bg-red-100 text-red-600',
            purple: 'bg-purple-100 text-purple-600',
          };
          const bgClass = colorClasses[step.color as keyof typeof colorClasses]?.split(' ')[0] || 'bg-gray-100';
          const textClass = colorClasses[step.color as keyof typeof colorClasses]?.split(' ')[1] || 'text-gray-600';
          return (
            <motion.div
              key={step.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow"
            >
              <div className="flex items-start gap-4">
                <div className={`w-12 h-12 ${bgClass} rounded-xl flex items-center justify-center flex-shrink-0`}>
                  <Icon className={`w-6 h-6 ${textClass}`} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-sm font-bold text-gray-500">Étape {step.id}</span>
                    <h4 className="text-xl font-bold text-gray-900">{step.title}</h4>
                  </div>
                  <p className="text-gray-700 mb-4">{step.description}</p>
                  <ul className="space-y-2">
                    {step.details.map((detail, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-gray-600">
                        <CheckCircle className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                        <span>{detail}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}

// Gouvernance
function GouvernanceTab() {
  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl p-6 border border-purple-200">
        <h3 className="text-2xl font-bold text-gray-900 mb-2">🛡️ Structure de Gouvernance</h3>
        <p className="text-gray-700">
          Rôles, responsabilités et processus décisionnels pour une gestion équilibrée des recevables.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
          <h4 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Users className="w-6 h-6 text-blue-600" />
            Rôles & Responsabilités
          </h4>
          <div className="space-y-4">
            <div>
              <h5 className="font-semibold text-gray-900 mb-2">Administration (Émilie/Mélissa)</h5>
              <ul className="space-y-1 text-gray-700 text-sm">
                <li>• Émission des factures</li>
                <li>• Suivi des échéances</li>
                <li>• Exécution des relances</li>
                <li>• Reporting des retards</li>
                <li>• Escalade selon procédure</li>
              </ul>
            </div>
            <div>
              <h5 className="font-semibold text-gray-900 mb-2">Ventes (Antoine/Jérôme)</h5>
              <ul className="space-y-1 text-gray-700 text-sm">
                <li>• Qualification client initiale</li>
                <li>• Communication avec client en retard</li>
                <li>• Négociation plans de paiement</li>
                <li>• Préservation relation client</li>
              </ul>
            </div>
            <div>
              <h5 className="font-semibold text-gray-900 mb-2">Direction</h5>
              <ul className="space-y-1 text-gray-700 text-sm">
                <li>• Approbation plans de paiement exceptionnels</li>
                <li>• Décision suspension/recouvrement</li>
                <li>• Arbitrage en cas de conflit</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
          <h4 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Shield className="w-6 h-6 text-purple-600" />
            Seuils d'Approbation
          </h4>
          <div className="space-y-4">
            <div className="bg-green-50 rounded-lg p-4 border border-green-200">
              <h5 className="font-semibold text-green-900 mb-2">Plan de paiement ≤ 30 jours</h5>
              <p className="text-sm text-green-700">Approbation : Administration</p>
            </div>
            <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
              <h5 className="font-semibold text-yellow-900 mb-2">Plan de paiement 31-60 jours</h5>
              <p className="text-sm text-yellow-700">Approbation : Administration + Ventes</p>
            </div>
            <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
              <h5 className="font-semibold text-orange-900 mb-2">Plan de paiement 61-90 jours</h5>
              <p className="text-sm text-orange-700">Approbation : Direction</p>
            </div>
            <div className="bg-red-50 rounded-lg p-4 border border-red-200">
              <h5 className="font-semibold text-red-900 mb-2">Suspension de service</h5>
              <p className="text-sm text-red-700">Approbation : Direction uniquement</p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
        <h4 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Clock className="w-6 h-6 text-indigo-600" />
          Réunions de Gouvernance
        </h4>
        <div className="space-y-3">
          <div className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
            <div>
              <h5 className="font-semibold text-gray-900">Réunion Mensuelle Recevables</h5>
              <p className="text-sm text-gray-600">Revue des comptes en retard, décisions d'escalade, ajustements procédures</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
            <div>
              <h5 className="font-semibold text-gray-900">Réunion Trimestrielle Stratégique</h5>
              <p className="text-sm text-gray-600">Analyse des KPIs, révision des termes de paiement, amélioration continue</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Esprit & Philosophie
function EspritTab() {
  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-6 border border-indigo-200">
        <h3 className="text-2xl font-bold text-gray-900 mb-2">💭 L'Esprit de la Procédure</h3>
        <p className="text-gray-700">
          Cette procédure n'est pas une punition. C'est un cadre de protection mutuelle qui respecte les deux réalités.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
          <h4 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Shield className="w-6 h-6 text-red-600" />
            L'Esprit Administration
          </h4>
          <p className="text-gray-700 mb-4 leading-relaxed">
            L'administration n'est <strong>pas "méchante"</strong>. Elle protège les dollars chèrement gagnés par toute l'équipe. 
            Chaque facture impayée, c'est du travail non rémunéré. C'est de la trésorerie qui manque pour payer les salaires, 
            investir dans l'équipement, ou croître.
          </p>
          <p className="text-gray-700 leading-relaxed">
            L'administration a besoin de <strong>rigueur</strong> et de <strong>processus clairs</strong> pour faire son travail 
            efficacement. Sans procédure, c'est le chaos. Avec procédure, c'est la protection.
          </p>
        </div>

        <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
          <h4 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Users className="w-6 h-6 text-blue-600" />
            L'Esprit Ventes
          </h4>
          <p className="text-gray-700 mb-4 leading-relaxed">
            Les ventes ne manquent <strong>pas de courage entrepreneurial</strong>. Elles doivent naviguer entre la protection 
            de BZ et la satisfaction client. Chaque relance agressive peut tuer une relation. Chaque flexibilité peut sauver 
            un partenariat à long terme.
          </p>
          <p className="text-gray-700 leading-relaxed">
            Les ventes ont besoin de <strong>flexibilité</strong> et de <strong>compréhension</strong> pour faire leur travail. 
            Sans nuance, c'est la rigidité. Avec nuance, c'est la relation.
          </p>
        </div>
      </div>

      <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl p-6 border border-emerald-200">
        <h4 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Scale className="w-6 h-6 text-emerald-600" />
          L'Équilibre BZ
        </h4>
        <p className="text-gray-700 leading-relaxed mb-4">
          Cette procédure crée un <strong>pont entre ces deux réalités</strong>. Elle donne à l'administration les outils 
          pour protéger BZ de manière structurée. Elle donne aux ventes un cadre pour négocier avec confiance. Elle crée 
          un langage commun où les deux équipes peuvent collaborer plutôt que s'opposer.
        </p>
        <p className="text-gray-700 leading-relaxed">
          <strong>L'objectif n'est pas de choisir un camp</strong>. C'est de créer un système où protection et relation 
          coexistent harmonieusement pour le bien de BZ et de ses clients.
        </p>
      </div>
    </div>
  );
}

// Psychologie & Impact
function PsychologieTab() {
  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-pink-50 to-rose-50 rounded-xl p-6 border border-pink-200">
        <h3 className="text-2xl font-bold text-gray-900 mb-2">🧠 Comprendre la Psychologie</h3>
        <p className="text-gray-700">
          Chaque équipe a ses motivations, ses peurs, et ses besoins. Comprendre cela, c'est créer de l'empathie et réduire les tensions.
        </p>
      </div>

      <div className="space-y-6">
        <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
          <h4 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Heart className="w-6 h-6 text-red-600" />
            La Psychologie de l'Administration
          </h4>
          <div className="space-y-4">
            <div>
              <h5 className="font-semibold text-gray-900 mb-2">💪 Forces & Motivations</h5>
              <ul className="space-y-1 text-gray-700 text-sm">
                <li>• <strong>Protection</strong> : Garder BZ financièrement sain</li>
                <li>• <strong>Rigueur</strong> : Faire les choses correctement</li>
                <li>• <strong>Responsabilité</strong> : Protéger les ressources de l'équipe</li>
                <li>• <strong>Clarté</strong> : Avoir des processus définis</li>
              </ul>
            </div>
            <div>
              <h5 className="font-semibold text-gray-900 mb-2">😰 Peurs & Frustrations</h5>
              <ul className="space-y-1 text-gray-700 text-sm">
                <li>• <strong>Manque de patience</strong> : Les retards créent du stress</li>
                <li>• <strong>Sentiment d'être "méchante"</strong> : Faire les relances est désagréable</li>
                <li>• <strong>Manque de support</strong> : Sentir que les ventes ne comprennent pas</li>
                <li>• <strong>Charge de travail</strong> : Gérer les retards prend du temps</li>
              </ul>
            </div>
            <div>
              <h5 className="font-semibold text-gray-900 mb-2">💡 Ce dont elle a besoin</h5>
              <ul className="space-y-1 text-gray-700 text-sm">
                <li>• <strong>Processus clairs</strong> : Savoir exactement quoi faire et quand</li>
                <li>• <strong>Support des ventes</strong> : Compréhension et collaboration</li>
                <li>• <strong>Reconnaissance</strong> : Comprendre qu'elle protège BZ</li>
                <li>• <strong>Outils efficaces</strong> : Automatisation pour réduire la charge</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
          <h4 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Heart className="w-6 h-6 text-blue-600" />
            La Psychologie des Ventes
          </h4>
          <div className="space-y-4">
            <div>
              <h5 className="font-semibold text-gray-900 mb-2">💪 Forces & Motivations</h5>
              <ul className="space-y-1 text-gray-700 text-sm">
                <li>• <strong>Relation client</strong> : Bâtir la confiance à long terme</li>
                <li>• <strong>Croissance</strong> : Développer les ventes</li>
                <li>• <strong>Flexibilité</strong> : S'adapter aux besoins clients</li>
                <li>• <strong>Résolution</strong> : Trouver des solutions gagnant-gagnant</li>
              </ul>
            </div>
            <div>
              <h5 className="font-semibold text-gray-900 mb-2">😰 Peurs & Frustrations</h5>
              <ul className="space-y-1 text-gray-700 text-sm">
                <li>• <strong>Perte de relation</strong> : Relances agressives = client fâché</li>
                <li>• <strong>Sentiment de manquer de courage</strong> : Ne pas oser négocier</li>
                <li>• <strong>Conflit avec admin</strong> : Sentir qu'on est "contre" l'admin</li>
                <li>• <strong>Complexité</strong> : Ne pas comprendre les processus admin</li>
              </ul>
            </div>
            <div>
              <h5 className="font-semibold text-gray-900 mb-2">💡 Ce dont elle a besoin</h5>
              <ul className="space-y-1 text-gray-700 text-sm">
                <li>• <strong>Cadre de négociation</strong> : Savoir ce qui est acceptable</li>
                <li>• <strong>Support de l'admin</strong> : Compréhension des contraintes client</li>
                <li>• <strong>Reconnaissance</strong> : Comprendre qu'elle protège la relation</li>
                <li>• <strong>Outils de communication</strong> : Templates et scripts professionnels</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
          <h4 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Brain className="w-6 h-6 text-indigo-600" />
            L'Impact de la Compréhension
          </h4>
          <p className="text-gray-700 leading-relaxed mb-4">
            Quand l'administration comprend que les ventes ne manquent pas de courage mais naviguent une réalité complexe, 
            elle peut être plus patiente. Quand les ventes comprennent que l'administration n'est pas "méchante" mais protège 
            BZ, elles peuvent être plus collaboratives.
          </p>
          <p className="text-gray-700 leading-relaxed">
            Cette procédure crée cet espace de compréhension mutuelle. Elle transforme un conflit potentiel en collaboration 
            structurée. Elle permet aux deux équipes de travailler ensemble plutôt que l'une contre l'autre.
          </p>
        </div>
      </div>
    </div>
  );
}

// Outils & Templates
function OutilsTab() {
  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-cyan-50 to-blue-50 rounded-xl p-6 border border-cyan-200">
        <h3 className="text-2xl font-bold text-gray-900 mb-2">🛠️ Outils & Templates</h3>
        <p className="text-gray-700">
          Ressources pratiques pour faciliter l'application de la procédure au quotidien.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
          <h4 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <FileText className="w-6 h-6 text-blue-600" />
            Templates de Communication
          </h4>
          <div className="space-y-3">
            <div className="bg-gray-50 rounded-lg p-4">
              <h5 className="font-semibold text-gray-900 mb-2">Rappel Pré-Échéance (J-7)</h5>
              <p className="text-sm text-gray-600 italic">
                "Bonjour [Client], Nous vous rappelons que votre facture #[Numéro] d'un montant de [Montant] est due le [Date]. 
                Merci de confirmer la réception et le traitement. Cordialement, [Nom]"
              </p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <h5 className="font-semibold text-gray-900 mb-2">Relance Post-Échéance (J+3)</h5>
              <p className="text-sm text-gray-600 italic">
                "Bonjour [Client], Votre facture #[Numéro] d'un montant de [Montant] était due le [Date]. Pourriez-vous nous 
                confirmer le statut du paiement ? Nous restons disponibles pour discuter. Cordialement, [Nom]"
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
          <h4 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Target className="w-6 h-6 text-green-600" />
            Grille de Qualification Client
          </h4>
          <div className="space-y-3">
            <div className="bg-green-50 rounded-lg p-4 border border-green-200">
              <h5 className="font-semibold text-green-900 mb-2">Risque Faible</h5>
              <p className="text-sm text-green-700">NET 30 jours standard</p>
            </div>
            <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
              <h5 className="font-semibold text-yellow-900 mb-2">Risque Moyen</h5>
              <p className="text-sm text-yellow-700">NET 30 jours avec garantie ou NET 45</p>
            </div>
            <div className="bg-red-50 rounded-lg p-4 border border-red-200">
              <h5 className="font-semibold text-red-900 mb-2">Risque Élevé</h5>
              <p className="text-sm text-red-700">Paiement anticipé ou garantie bancaire</p>
            </div>
          </div>
        </div>
      </div>

      {/* Note stratégique - Dany Bourassa (DDC) */}
      <div className="bg-white rounded-xl p-6 border border-amber-200 shadow-sm">
        <div className="flex items-start gap-4 mb-4">
          <div className="w-12 h-12 rounded-full overflow-hidden bg-amber-100 flex items-center justify-center flex-shrink-0">
            <img
              src="/DB.png"
              alt="Dany Bourassa"
              className="w-full h-full object-cover"
            />
          </div>
          <div>
            <h4 className="text-xl font-bold text-gray-900 mb-1">
              Note stratégique de Dany Bourassa (Directeur du Développement Commercial)
            </h4>
            <p className="text-sm text-gray-600">
              À prendre en considération dans chaque communication et dans le suivi des dossiers sensibles.
            </p>
          </div>
        </div>

        <div className="space-y-3 text-sm text-gray-700">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <h5 className="font-semibold text-amber-900 mb-1">Message clé dans le courriel</h5>
            <p className="leading-relaxed">
              Ajouter en haut du courriel, en <strong className="text-red-600">rouge</strong> et bien visible : 
              <strong> « Ne pas tenir compte du message si le paiement a été finalisé »</strong>. 
              L'objectif est de rassurer le client qui a déjà payé, tout en maintenant la rigueur du processus.
            </p>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h5 className="font-semibold text-blue-900 mb-1">Explication par le DDC</h5>
            <p className="leading-relaxed">
              Le DDC explique que le <strong>processus ne sera pas arrêté tant que le paiement n'est pas réellement reçu</strong>. 
              Donner des exemples concrets (délais de chèque vs virement bancaire) pour que le client comprenne pourquoi il peut 
              encore recevoir des relances même après avoir initié un paiement.
            </p>
          </div>

          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
            <h5 className="font-semibold text-emerald-900 mb-1">Retour et suivi du DDC</h5>
            <p className="leading-relaxed">
              Le DDC revient vers l'équipe avec la <strong>possibilité de règlement</strong> (mode, date, conditions), confirme 
              le règlement convenu avec le client et <strong>note la discussion dans le dossier</strong> (CRM / PRIsME) pour assurer la traçabilité.
            </p>
          </div>

          <div className="bg-violet-50 border border-violet-200 rounded-lg p-4">
            <h5 className="font-semibold text-violet-900 mb-1">Confirmation & cote client</h5>
            <p className="leading-relaxed">
              Chaque règlement dans un dossier problématique doit être <strong>confirmé</strong> et archivé. Ces informations 
              alimentent la <strong>cote client</strong> (comportement de paiement, fiabilité, respect des engagements) qui servira 
              à ajuster les termes de paiement et le niveau de risque accordé au client.
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
        <h4 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
          <TrendingUp className="w-6 h-6 text-purple-600" />
          Systèmes & Automatisation
        </h4>
        <ul className="space-y-2 text-gray-700">
          <li className="flex items-start gap-2">
            <CheckCircle className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
            <span>ERP avec module recevables intégré</span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
            <span>Alertes automatiques J-7, J-3, J+3, J+7, J+15, J+30</span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
            <span>Dashboard recevables en temps réel</span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
            <span>Intégration avec services de qualification (D&B, Equifax)</span>
          </li>
        </ul>
      </div>
    </div>
  );
}

// KPIs & Métriques
function KPIsTab() {
  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-6 border border-purple-200">
        <h3 className="text-2xl font-bold text-gray-900 mb-2">📊 KPIs & Métriques</h3>
        <p className="text-gray-700">
          Indicateurs clés pour mesurer l'efficacité de la gestion des recevables.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
          <h4 className="text-xl font-bold text-gray-900 mb-4">Indicateurs Principaux</h4>
          <div className="space-y-4">
            <div>
              <h5 className="font-semibold text-gray-900 mb-1">DSO (Days Sales Outstanding)</h5>
              <p className="text-sm text-gray-600">Objectif : &lt; 35 jours</p>
              <p className="text-xs text-gray-500 mt-1">Temps moyen de recouvrement</p>
            </div>
            <div>
              <h5 className="font-semibold text-gray-900 mb-1">Taux de Recouvrement</h5>
              <p className="text-sm text-gray-600">Objectif : &gt; 98%</p>
              <p className="text-xs text-gray-500 mt-1">% des factures recouvrées</p>
            </div>
            <div>
              <h5 className="font-semibold text-gray-900 mb-1">Délai Moyen de Paiement</h5>
              <p className="text-sm text-gray-600">Objectif : &lt; 32 jours</p>
              <p className="text-xs text-gray-500 mt-1">Temps réel de paiement client</p>
            </div>
            <div>
              <h5 className="font-semibold text-gray-900 mb-1">Taux de Défaut</h5>
              <p className="text-sm text-gray-600">Objectif : &lt; 1%</p>
              <p className="text-xs text-gray-500 mt-1">% de factures non recouvrées</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
          <h4 className="text-xl font-bold text-gray-900 mb-4">Indicateurs Secondaires</h4>
          <div className="space-y-4">
            <div>
              <h5 className="font-semibold text-gray-900 mb-1">Nombre de Relances Moyennes</h5>
              <p className="text-sm text-gray-600">Objectif : &lt; 2 relances/facture</p>
            </div>
            <div>
              <h5 className="font-semibold text-gray-900 mb-1">Taux de Paiement à l'Échéance</h5>
              <p className="text-sm text-gray-600">Objectif : &gt; 70%</p>
            </div>
            <div>
              <h5 className="font-semibold text-gray-900 mb-1">Montant en Retard</h5>
              <p className="text-sm text-gray-600">Objectif : &lt; 5% du CA mensuel</p>
            </div>
            <div>
              <h5 className="font-semibold text-gray-900 mb-1">Satisfaction Équipes</h5>
              <p className="text-sm text-gray-600">Objectif : &gt; 4/5</p>
              <p className="text-xs text-gray-500 mt-1">Sondage trimestriel Admin + Ventes</p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
        <h4 className="text-xl font-bold text-gray-900 mb-4">📈 Révision Trimestrielle</h4>
        <p className="text-gray-700 leading-relaxed">
          Chaque trimestre, l'équipe Admin + Ventes + Direction se réunit pour analyser ces KPIs, identifier les tendances, 
          et ajuster les procédures si nécessaire. Cette révision continue assure que la procédure reste efficace et équilibrée.
        </p>
      </div>
    </div>
  );
}

